import React, { useEffect, useState } from 'react';
import { DateRangePicker } from 'react-date-range';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { enUS } from 'date-fns/locale';
import { format } from 'date-fns';
import {
    clientStatusEnums,
    renderClientName,
    renderStaffName,
    SaveParams,
    staffStatusEnums,
    getApptErrorOptions,
    useDiagnosisMatchEnabled,
    Images,
    makeCptServiceOptionId,
} from 'utils';
import {
    CheckboxesTags,
    MultiStaffBox,
    SelectAutocompletePaginationMultiple,
    SimpleTooltip,
    TextRow,
} from 'components';
import { ACTIVE_CLIENT_STAFF_TOOLTIP, scheduleStatusEnums, scheduleTypes } from '../constants';
import { localSaveScheduleDates } from '../../constants';
import { Checkbox } from '@mui/material';

export const CalendarFilters = ({ getEvents }) => {
    const diagnosisMatchEnabled = useDiagnosisMatchEnabled();
    const { appointments, calendarAppointments } = useSelector((state) => ({
        appointments: state.appointment.appointments,
        calendarAppointments: state.appointment.calendarAppointments,
    }));
    const globalServices = useSelector((state) => state.fundingSource.fundingUniqueServices);
    const navigate = useNavigate();
    const location = useLocation();
    const info = location?.state;
    const [state, setState] = useState([
        {
            startDate: new Date(),
            endDate: new Date(),
            key: 'selection',
        },
    ]);
    const customLocale = {
        ...enUS,
        options: {
            ...enUS.options,
            weekStartsOn: 0,
        },
    };

    // Parse a YYYY-MM-DD string (or moment) into a local-midnight Date object.
    // This prevents the DateRangePicker from shifting dates by one day in
    // western-hemisphere timezones (new Date('YYYY-MM-DD') parses as UTC midnight
    // which becomes the previous day in US timezones).
    const toLocalDate = (dateStr) => {
        const m = moment(dateStr);
        return new Date(m.year(), m.month(), m.date());
    };

    useEffect(() => {
        if (info?.firstDate && info?.lastDate) {
            setState([
                {
                    startDate: toLocalDate(info.firstDate),
                    endDate: toLocalDate(info.lastDate),
                    key: 'selection',
                },
            ]);
        } else {
            const currentDate = moment();
            const currentWeekStart = currentDate.clone().startOf('week');
            const currentWeekEnd = currentDate.clone().endOf('week');
            const startDate = moment(info?.firstDate ? info?.firstDate : currentWeekStart);
            const endDate = moment(info?.lastDate ? info?.lastDate : currentWeekEnd);

            setState([
                {
                    startDate: toLocalDate(startDate),
                    endDate: toLocalDate(endDate),
                    key: 'selection',
                },
            ]);
        }
    }, [info]);

    const handleDateChange = (item) => {
        setState([item.selection]);

        const startDate = moment(item?.selection?.startDate).format('YYYY-MM-DD');
        const endDate = moment(item?.selection?.endDate).format('YYYY-MM-DD');

        const params = {
            ...info,
        };
        params.firstDate = startDate;
        params.lastDate = endDate;
        localSaveScheduleDates({
            firstDate: startDate,
            lastDate: endDate,
        });
        SaveParams(navigate, params);
    };

    const handleSelectValue = (list, name) => {
        const params = {
            ...info,
        };
        params.page = 1;
        params.skip = 0;
        list?.length ? (params[name] = list) : delete params[name];
        SaveParams(navigate, { ...params });
    };



    const handleChange = (e) => {
        const newFilters = {
            ...info,
        };
        newFilters.page = 1;
        newFilters.skip = 0;

        if (e?.target?.value === 'All') {
            delete newFilters[e.target.name];
        } else {
            newFilters[e.target.name] = e.target.value;
        }
        SaveParams(navigate, { ...newFilters });
    };

    const changeMultiple = (e) => {
        const newFilters = {
            ...info,
        };
        newFilters.page = 1;
        newFilters.skip = 0;

        if (e?.target?.value === 'All') {
            delete newFilters[e.target.name];
        } else {
            newFilters[e.target.name] = e.target.value;
            if (e.target.name === 'staffs' || e.target.name === 'clients') {
                newFilters[e.target.name === 'staffs' ? 'staffFull' : 'clientFull'] = e.target?.full;
            }
        }

        if(e.target.name === 'staffs' && newFilters?.ownStaff) {
            delete newFilters.ownStaff
        }

        SaveParams(navigate, { ...newFilters });
    };

    function customDayContent(day) {
        let extraDot = null;
        const apptIsDefined = (calendarAppointments || [])?.find((appt) => moment(appt?.date).isSame(day, 'day') && appt?.hasAppt);

        if (apptIsDefined) {
            extraDot = (
                <div
                    style={{
                        height: '5px',
                        width: '5px',
                        borderRadius: '100%',
                        background: '#12B76A',
                        position: 'absolute',
                        bottom: 2,
                        left: 3,
                    }}
                />
            );
        }
        return (
            <div style={{ position: 'relative' }}>
                {extraDot}
                <span>{format(day, 'dd')}</span>
            </div>
        );
    }

    const getMonthApptInfo = (month) => {
        getEvents('noLoad', month);
    };

    return (
        <div className="calendar-filters">
            <div className="calendar-filters-wrapper">
                <DateRangePicker
                    showDateDisplay={false}
                    ranges={state}
                    onChange={handleDateChange}
                    showMonthAndYearPickers={false}
                    showSelectionPreview={false}
                    weekdayDisplayFormat={'EEEEE'}
                    style={{ width: '250px' }}
                    locale={customLocale}
                    showTrailingAndLeadingDates
                    dayContentRenderer={customDayContent}
                    onShownDateChange={(date) => getMonthApptInfo(date)}
                />
                <div className="filters-wrapper-row">
                    <div style={{ marginBottom: 16 }}>
                        <SelectAutocompletePaginationMultiple
                            defaultStatuses={!info?.showActive ? [staffStatusEnums.ACTIVE] : null}
                            outLabel={'Staff'}
                            uiType={'tableFilter'}
                            placeholder={info?.staffFull?.length ? `${info?.staffFull?.length} staff selected` : 'Search Staff'}
                            // placeholder={info?.staffFull?.length ? info?.staffFull?.map((i) => renderStaffName(i)).join(', ') : 'Search Staff'}
                            name={'staffs'}
                            handleSelect={changeMultiple}
                            defaultValue={info?.staffFull ? info?.staffFull : []}
                            renderValue={(i) => renderStaffName(i)}
                            type={'staff'}
                            autoSelect={true}
                            noError={true}
                        />
                        {!!info?.staffFull?.length && (
                            <MultiStaffBox
                                list={info?.staffFull}
                                removeAll={() => {
                                    const params = { ...info };
                                    delete params.staffFull;
                                    delete params.staffs;
                                    SaveParams(navigate, params);
                                }}
                                removeSingle={(i) => {
                                    const params = {
                                        ...info,
                                    };
                                    params.staffFull = info?.staffFull?.filter((staff) => staff?.id !== i?.id);
                                    params.staffs = info?.staffs?.filter((staff) => staff !== i?.id);
                                    SaveParams(navigate, params);
                                }}
                            />
                        )}
                    </div>
                    <div>
                        <div style={{ marginBottom: 16 }}>
                            <SelectAutocompletePaginationMultiple
                                defaultStatuses={
                                    !info?.showActive
                                        ? [
                                              clientStatusEnums.ACTIVE,
                                              clientStatusEnums.FBA,
                                              clientStatusEnums.ON_HOLD,
                                              clientStatusEnums.WAIT_LIST,
                                          ]
                                        : null
                                }
                                outLabel={'Client'}
                                uiType={'tableFilter'}
                                placeholder={info?.clientFull?.length ? `${info?.clientFull?.length} client selected` : 'Search Client'}
                                // placeholder={info?.clientFull?.length ? info?.clientFull?.map((i) => renderClientName(i)).join(', ') : 'Search client'}
                                name={'clients'}
                                handleSelect={changeMultiple}
                                defaultValue={info?.clientFull ? info?.clientFull : []}
                                renderValue={(i) => renderClientName(i)}
                                type={'client'}
                                autoSelect={true}
                                noError={true}
                            />
                            {!!info?.clientFull?.length && (
                                <MultiStaffBox
                                    type={'client'}
                                    list={info?.clientFull}
                                    removeAll={() => {
                                        const params = { ...info };
                                        delete params.clientFull;
                                        delete params.clients;
                                        SaveParams(navigate, params);
                                    }}
                                    removeSingle={(i) => {
                                        const params = {
                                            ...info,
                                        };
                                        params.clientFull = info?.clientFull?.filter((client) => client?.id !== i?.id);
                                        params.clients = info?.clients?.filter((client) => client !== i?.id);
                                        SaveParams(navigate, params);
                                    }}
                                />
                            )}
                        </div>
                    </div>
                    <CheckboxesTags
                        handleChange={(e) => handleSelectValue(e, 'types')}
                        name="types"
                        renderValue={(i) => i?.name}
                        value={info?.types ? info?.types : []}
                        permissionsList={scheduleTypes}
                        outLabel={'Event Type'}
                        placeholder={info?.types?.length ? info?.types?.map((i) => i?.name).join(', ') : 'All'}
                        uiType={'tableFilter'}
                        notShowError={true}
                        styles={{ width: 'auto', marginBottom: '20px' }}
                    />
                    <CheckboxesTags
                        handleChange={(e) => handleSelectValue(e, 'statuses')}
                        name="statuses"
                        renderValue={(i) => i?.name}
                        value={info?.statuses ? info?.statuses : []}
                        permissionsList={scheduleStatusEnums}
                        outLabel={'Event Status'}
                        placeholder={info?.statuses?.length ? info?.statuses?.map((i) => i?.name).join(', ') : 'All'}
                        uiType={'tableFilter'}
                        notShowError={true}
                        styles={{ width: 'auto', marginBottom: '20px' }}
                    />
                    <CheckboxesTags
                        handleChange={(e) => handleSelectValue(e, 'errors')}
                        name="errors"
                        renderValue={(i) => i?.name}
                        value={info?.errors ? info?.errors : []}
                        permissionsList={getApptErrorOptions(diagnosisMatchEnabled)}
                        outLabel={'Errored'}
                        placeholder={info?.errors?.length ? info?.errors?.map((i) => i?.name).join(', ') : 'All'}
                        uiType={'tableFilter'}
                        notShowError={true}
                        styles={{ width: 'auto', marginBottom: '20px' }}
                    />

                    <CheckboxesTags
                        handleChange={(e) => handleSelectValue(e, 'cptCode')}
                        name="cptCode"
                        renderValue={(i) => i?.name}
                        value={info?.cptCode ? info?.cptCode : []}
                        permissionsList={
                            globalServices?.map((i) => ({
                                name: `${i?.serviceTypeName} (${i?.cptCode})`,
                                // Encode both cptCode and serviceTypeId so the backend can pair them
                                id: makeCptServiceOptionId(i?.cptCode, i?.serviceTypeId),
                            })) || []
                        }
                        outLabel={'Service'}
                        placeholder={info?.cptCode?.length ? info?.cptCode?.map((i) => i?.name).join(', ') : 'All'}
                        uiType={'tableFilter'}
                        notShowError={true}
                        styles={{ width: 'auto' }}
                    />

                    {info?.viewType === 'calendar' && (
                        <div className="flex-column-start" style={{ gap: 16, marginTop: 16 }}>
                            <div className="check-box-filter" style={{ marginLeft: 0 }}>
                                <Checkbox
                                    onClick={() =>
                                        handleChange({
                                            target: {
                                                value: !info?.withoutNotes,
                                                name: 'withoutNotes',
                                            },
                                        })
                                    }
                                    style={{
                                        padding: 0,
                                        color: info?.withoutNotes ? '#347AF0' : '#D0D5DD',
                                        borderWidth: 1,
                                    }}
                                    checked={info?.withoutNotes || false}
                                />
                                <p style={{ margin: 0 }}>w/o notes</p>
                            </div>
                            <div className="check-box-filter" style={{ marginLeft: 0 }}>
                                <Checkbox
                                    onClick={() =>
                                        handleChange({
                                            target: {
                                                value: !info?.withoutSignature,
                                                name: 'withoutSignature',
                                            },
                                        })
                                    }
                                    style={{
                                        padding: 0,
                                        color: info?.withoutSignature ? '#347AF0' : '#D0D5DD',
                                        borderWidth: 1,
                                    }}
                                    checked={info?.withoutSignature || false}
                                />
                                <p style={{ margin: 0 }}>w/o sign</p>
                            </div>
                            <div className="check-box-filter" style={{ marginLeft: 0 }}>
                                <Checkbox
                                    onClick={() =>
                                        handleChange({
                                            target: {
                                                value: !info?.withoutClientSignature,
                                                name: 'withoutClientSignature',
                                            },
                                        })
                                    }
                                    style={{
                                        padding: 0,
                                        color: info?.withoutClientSignature ? '#347AF0' : '#D0D5DD',
                                        borderWidth: 1,
                                    }}
                                    checked={info?.withoutClientSignature || false}
                                />
                                <p style={{ margin: 0 }}>w/o client sign</p>
                            </div>
                            <div className="check-box-filter" style={{ marginLeft: 0 }}>
                                <Checkbox
                                    onClick={() =>
                                        handleChange({
                                            target: {
                                                value: !info?.withoutProviderSignature,
                                                name: 'withoutProviderSignature',
                                            },
                                        })
                                    }
                                    style={{
                                        padding: 0,
                                        color: info?.withoutProviderSignature ? '#347AF0' : '#D0D5DD',
                                        borderWidth: 1,
                                    }}
                                    checked={info?.withoutProviderSignature || false}
                                />
                                <p style={{ margin: 0 }}>w/o provider sign</p>
                            </div>

                            <div className="check-box-filter" style={{ marginLeft: 0 }}>
                                <Checkbox
                                    onClick={() =>
                                        handleChange({
                                            target: {
                                                value: !info?.supervision,
                                                name: 'supervision',
                                            },
                                        })
                                    }
                                    style={{
                                        padding: 0,
                                        color: info?.supervision ? '#347AF0' : '#D0D5DD',
                                        borderWidth: 1,
                                    }}
                                    checked={info?.supervision || false}
                                />
                                <p style={{ margin: 0 }}>Supervision</p>
                            </div>
                            <SimpleTooltip
                                title={
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        <p>{ACTIVE_CLIENT_STAFF_TOOLTIP.ON}</p>
                                        <p>{ACTIVE_CLIENT_STAFF_TOOLTIP.OFF}</p>
                                    </div>
                                }
                                placement="right">
                                <div className="check-box-filter" style={{ width: 'fit-content' }}>
                                    <Checkbox
                                        onClick={() =>
                                            handleChange({
                                                target: {
                                                    value: !info?.showActive,
                                                    name: 'showActive',
                                                },
                                            })
                                        }
                                        style={{
                                            padding: 0,
                                            color: !info?.showActive ? '#347AF0' : '#D0D5DD',
                                            borderWidth: 1,
                                        }}
                                        checked={!info?.showActive}
                                    />
                                    <p>Active clients/staff only</p>
                                </div>
                            </SimpleTooltip>
                            {/*<div className="multi-staff-view-box">*/}
                            {/*    <CustomizedSwitch*/}
                            {/*        checked={info?.multiStaffView}*/}
                            {/*        handleClick={() => handleChange({*/}
                            {/*            target: {*/}
                            {/*                value: !info?.multiStaffView,*/}
                            {/*                name: 'multiStaffView',*/}
                            {/*            },*/}
                            {/*        })}*/}
                            {/*    />*/}
                            {/*    <p className="multi-staff-view-text">Multi-Staff View</p>*/}
                            {/*</div>*/}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
